import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import { useOrder, Service } from '../../context/OrderContext'
import { usePartnerAuth } from '../../context/PartnerAuthContext'
import Stepper from '../../components/Stepper'

/**
 * Page de sélection du service dans le tunnel partenaire.
 * Réutilise la même logique que ServiceSelectionPage mais avec le branding partenaire.
 */
const PartnerServicePage: React.FC = () => {
  const navigate = useNavigate()
  const { setService, goToStep, draft } = useOrder()
  const { partner } = usePartnerAuth()

  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Vérifier qu'on est en mode partenaire
    if (!draft.isPartnerMode) {
      navigate('/partner/book')
      return
    }

    const loadServices = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('services')
          .select('*')
          .eq('is_active', true)
          .order('name', { ascending: true })

        if (error) {
          setError('Erreur lors du chargement des services.')
          setServices([])
        } else {
          setServices((data ?? []) as Service[])
        }
      } catch (e) {
        setError('Erreur inattendue.')
      } finally {
        setLoading(false)
      }
    }

    goToStep('SERVICE')
    void loadServices()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectService = (service: Service) => {
    setService(service)
    navigate('/partner/book/options')
  }

  return (
    <div className="card">
      <Stepper />

      {/* Bannière partenaire */}
      {partner && (
        <div
          data-testid="partner-banner"
          style={{
            background: `${partner.color_code}22`,
            border: `1px solid ${partner.color_code}`,
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          {partner.logo_url && (
            <img
              src={partner.logo_url}
              alt={partner.name}
              style={{ height: 28, borderRadius: 4 }}
            />
          )}
          <span style={{ fontWeight: 600, color: partner.color_code }}>
            Réservation {partner.name}
          </span>
        </div>
      )}

      <div className="card-header">
        <h1 className="card-title">Choisis ton service</h1>
      </div>

      {error && (
        <p style={{ color: '#f87171', marginTop: '0.5rem' }}>{error}</p>
      )}

      {loading ? (
        <p>Chargement des services…</p>
      ) : (
        <div className="grid-2" style={{ marginTop: '1rem' }}>
          {services.map(service => (
            <button
              key={service.id}
              type="button"
              data-testid={`service-button-${service.id}`}
              className="card"
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                border: '1px solid rgba(148,163,184,0.3)',
                padding: 0,
                overflow: 'hidden',
              }}
              onClick={() => handleSelectService(service)}
            >
              {service.image_url && (
                <img
                  data-testid={`service-image-${service.id}`}
                  src={service.image_url}
                  alt={service.name}
                  style={{
                    width: '100%',
                    height: 140,
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              )}
              <div style={{ padding: '1.25rem' }}>
                <div className="card-header" style={{ marginBottom: '.5rem' }}>
                  <h2 className="card-title" style={{ fontSize: '1rem' }}>
                    {service.name}
                  </h2>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#22c55e', marginLeft: 'auto' }}>
                    {service.base_price.toFixed(2)} CHF
                  </div>
                </div>
                <p style={{ fontSize: '.9rem', color: '#9ca3af', lineHeight: 1.4 }}>
                  {service.description || "Service complet de cordage."}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default PartnerServicePage
